import { useEffect, useRef } from 'react';

/**
 * A motion token of workspace.css (`--ws-motion`) in milliseconds, `fallback` when it is missing. The production build
 * writes `220ms` as `.22s`, so the unit counts: read with `parseFloat` alone, every dialog and result animation there
 * lasted 0.22 ms.
 */
export function motionMs(style: CSSStyleDeclaration, token: string, fallback: number): number {
  const value = style.getPropertyValue(token).trim(), amount = parseFloat(value);
  if (!Number.isFinite(amount)) return fallback;
  return value.endsWith('ms') ? amount : value.endsWith('s') ? amount * 1000 : amount;
}

/** Animate only the current result surface; its controls and item identity stay mounted. */
export function useContentMotion(identity: string) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = ref.current;
    const preference = matchMedia('(prefers-reduced-motion: reduce)');
    if (!element || preference.matches) return;
    const style = getComputedStyle(element);
    const animation = element.animate([{ opacity: .45, transform: 'translateY(4px)' }, { opacity: 1, transform: 'none' }], { duration: motionMs(style, '--ws-motion', 220), easing: style.getPropertyValue('--ws-ease').trim() });
    const changed = () => { if (preference.matches) animation.cancel(); };
    preference.addEventListener('change', changed);
    return () => { animation.cancel(); preference.removeEventListener('change', changed); };
  }, [identity]);
  return ref;
}
